import type { Reimbursement } from "@/types";

/**
 * The fields a re-upload of a carrier file matches an existing reimbursement on
 * (`ReimbursementNaturalKey` in coronis-apps, #7560). Changing one on a file-loaded row,
 * including filling one that loaded blank, makes the next re-upload of that file insert
 * a duplicate instead of matching, so the API rejects the change (409) and the page
 * shows them read-only. Payment Date and Check Date are not in the key and stay editable,
 * which is what lets a loaded reimbursement be moved to the right Claims Summary period.
 */
const LOAD_KEY_FIELDS: ReadonlySet<string> = new Set([
  "reimbursement_amount",
  "pended_amount",
  "provider",
  "service_start_date",
  "received_date",
  "check_number",
  "payment_reference",
]);

const LOAD_KEY_LOCK_REASON =
  "Loaded from the carrier file. Re-uploads of the file are matched on this field, so it can't be edited.";

/** A reimbursement came from a document when it carries document provenance. */
export const isFileLoaded = (record?: Pick<Reimbursement, "source_documents"> | null): boolean =>
  (record?.source_documents?.length ?? 0) > 0;

/** The read-only reason for `source` on this record, or undefined when it is editable. */
export const loadKeyLockReason = (
  record: Pick<Reimbursement, "source_documents"> | null | undefined,
  source: string
): string | undefined => (isFileLoaded(record) && LOAD_KEY_FIELDS.has(source) ? LOAD_KEY_LOCK_REASON : undefined);
